import React, { useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

const MAX_BULTOS = 999;

interface NotaEnvioDialogProps {
  open: boolean;
  comprobante: string;
  cliente: string;
  bultosSugeridos: number | null;
  cargandoSugerencia: boolean;
  loading: boolean;
  onClose: () => void;
  onConfirm: (bultos: number) => void;
}

const NotaEnvioDialog: React.FC<NotaEnvioDialogProps> = ({
  open,
  comprobante,
  cliente,
  bultosSugeridos,
  cargandoSugerencia,
  loading,
  onClose,
  onConfirm,
}) => {
  const [bultos, setBultos] = useState('1');

  useEffect(() => {
    if (open) setBultos(String(bultosSugeridos ?? 1));
  }, [open, bultosSugeridos]);

  const cantidad = Number(bultos);
  const valido = Number.isInteger(cantidad) && cantidad >= 1 && cantidad <= MAX_BULTOS;
  const ocupado = loading || cargandoSugerencia;

  const confirmar = () => {
    if (valido && !ocupado) onConfirm(cantidad);
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ color: '#1976d2', fontWeight: 600 }}>Nota de envío</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Remito <strong>{comprobante}</strong> · {cliente}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Cantidad de bultos"
            type="number"
            value={bultos}
            disabled={cargandoSugerencia}
            onChange={(e) => setBultos(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                confirmar();
              }
            }}
            error={!valido}
            helperText={
              cargandoSugerencia
                ? 'Calculando bultos del remito...'
                : valido
                  ? 'Por defecto, la suma de las cantidades del remito. Se imprime una etiqueta por bulto'
                  : `Ingresá un número entero entre 1 y ${MAX_BULTOS}`
            }
            inputProps={{ min: 1, max: MAX_BULTOS, step: 1 }}
            InputProps={{
              endAdornment: cargandoSugerencia ? <CircularProgress size={18} /> : undefined,
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={confirmar}
          disabled={!valido || ocupado}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <LocalOfferIcon />}
          sx={{ textTransform: 'none' }}
        >
          Generar etiquetas
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NotaEnvioDialog;
